import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../scripts/AuthContext";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import { useSocialProfile } from "../scripts/SocialProfileContext";
import { useSearchParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { getFullName } from "@/lib/userUtils";
import { toastError, toastSuccess } from "@/lib/toast";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import PostCard from "./PostCard";
import PostComposer from "./PostComposer";
import CatchUpCard from "./CatchUpCard";
import SocialHomeRail, {
  SocialHomeDiscoveryStrip,
} from "./SocialHomeRail";
import SocialHomeLeftRail from "./SocialHomeLeftRail";
import SocialHomeMobileMore from "./SocialHomeMobileMore";
import { useSocialPaths } from "@/lib/socialPaths";
import { permalinkForPost, withReturnQuery, pathWithPostAnchor, feedPostAnchorId, socialThreadHref } from "@/lib/permalinks";
import { useScrollToFeedPostAnchor } from "@/lib/useScrollToFeedPostAnchor";
import { prependCreatedPost, mergePinnedFeed, normalizeFeedResponse } from "@/lib/feedPosts";
import { uploadPostAttachments } from "@/lib/postAttachments";
import { uploadSocialPostMedia, setSocialEmbed } from "@/lib/socialMedia";
import { seedLikedFromPosts } from "@/lib/socialFeedLikes";

import { API_BASE_URL } from "@/lib/apiBase";
import { fetchUserMe } from "@/lib/userMeCache";
import { normalizeSocialFeedPost } from "@/lib/normalizeSocialFeedPost";

const FEED_PAGE_SIZE = 50;

const Dashboard = () => {
  const { accessToken } = useAuth(); // Access token from context
  const {
    selectedOrgId,
    setSelectedOrgId,
    workspaceReady,
    workspaceUpdating,
    workspaceRefreshKey,
    workspaceSnapshot,
  } = useOrgFilter(); // Get selected org filter from context
  const { profile: socialProfile } = useSocialProfile();
  const socialPaths = useSocialPaths();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [highlightedPostId, setHighlightedPostId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [interactionCounts, setInteractionCounts] = useState({}); // Cache for postId -> {likeCount, commentCount}
  const [likedPosts, setLikedPosts] = useState(new Set()); // Track which posts current user has liked
  const [orgMembersMaps, setOrgMembersMaps] = useState({}); // Map of orgId -> { userId -> title }
  const [composerDestinations, setComposerDestinations] = useState([]);
  const [canOrgPostCritical, setCanOrgPostCritical] = useState(false);
  const [canManageOrgSettings, setCanManageOrgSettings] = useState(false);
  const [adminTeamIds, setAdminTeamIds] = useState(() => new Set());
  const [isCreatingPost, setIsCreatingPost] = useState(false);
  const [composerFormKey, setComposerFormKey] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [socialSuggestions, setSocialSuggestions] = useState(null);
  /** Soft-bias ref for org feed catchup_window=1 (synced from CatchUpCard). */
  const catchupBehindRef = useRef(false);

  // Number of non-pinned feed posts already loaded (the next page's offset).
  const feedOffsetRef = useRef(0);
  // Ids currently in the feed, kept in sync so load-more can dedupe cheaply.
  const seenIdsRef = useRef(new Set());
  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);

  axios.defaults.withCredentials = true;

  // Helper function to make authenticated API calls with token refresh
  // Interceptors handle token refresh; this just runs the request with the current token
  const makeAuthenticatedRequest = async (requestFn, token = accessToken) => {
    return requestFn(token);
  };

  const fetchUserData = async (token) => {
    return fetchUserMe(token);
  };

  const isSocialWorkspace =
    selectedOrgId === "social" || selectedOrgId === null;
  const postsApiBase = isSocialWorkspace
    ? `${API_BASE_URL}/social/posts`
    : `${API_BASE_URL}/posts`;
  const interactionsDeleteBase = isSocialWorkspace
    ? `${API_BASE_URL}/social/posts/interactions`
    : `${API_BASE_URL}/posts/interactions`;

  const normalizeSocialHomePost = (p) => normalizeSocialFeedPost(p);

  /** Nested original inside a quote (or bare-of-quote). */
  const socialQuoteEmbedRoot = (post) => {
    if (post?.repost_kind === "quote" && post.repost_of) return post.repost_of;
    if (
      post?.repost_kind === "bare" &&
      post.repost_of?.repost_kind === "quote" &&
      post.repost_of.repost_of
    ) {
      return post.repost_of.repost_of;
    }
    return null;
  };

  const socialLikeTargetId = (post) => {
    if (post?.repost_kind === "bare" && post.repost_of) {
      return post.repost_of.social_post_id ?? post.repost_of.post_id;
    }
    return post?.post_id ?? post?.social_post_id;
  };

  const fetchFeed = async (token, orgId, offset = 0, options = {}) => {
    // If orgId is "social" or null, fetch personal feed
    // Otherwise, fetch org-specific feed
    const isSocialFeed = orgId === "social" || orgId === null;
    const endpoint = isSocialFeed 
      ? `${API_BASE_URL}/social/feed`
      : `${API_BASE_URL}/posts/feed`;
    
    const params = {
      limit: FEED_PAGE_SIZE,
      offset,
    };
    
    // Add orgId for org feed (required)
    if (!isSocialFeed) {
      params.orgId = orgId;
      if (options.catchupWindow) {
        params.catchup_window = 1;
      }
    }
    
    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params,
    });
    return response.data;
  };

  // Fetch org members for a single organization
  const fetchOrgMembers = async (orgId, token) => {
    try {
      if (
        workspaceSnapshot?.orgId === orgId &&
        Array.isArray(workspaceSnapshot.members)
      ) {
        return workspaceSnapshot.members;
      }
      const response = await axios.get(`${API_BASE_URL}/org/${orgId}/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data || [];
    } catch (err) {
      console.error(`Failed to fetch members for org ${orgId}:`, err);
      return [];
    }
  };

  const loadComposerDestinations = async (orgId, token, currentUserId) => {
    if (!orgId || orgId === "social") {
      setComposerDestinations([]);
      setCanOrgPostCritical(false);
      setCanManageOrgSettings(false);
      setAdminTeamIds(new Set());
      return;
    }

    try {
      let membershipsData;
      let meData;
      if (
        workspaceSnapshot?.orgId === orgId &&
        (workspaceSnapshot.teams || workspaceSnapshot.me)
      ) {
        membershipsData = workspaceSnapshot.teams || [];
        meData = workspaceSnapshot.me || null;
      } else {
        const [membershipsRes, meRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/team/memberships`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { org_id: orgId },
          }),
          axios
            .get(`${API_BASE_URL}/org/${orgId}/me`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .catch(() => null),
        ]);
        membershipsData = membershipsRes.data;
        meData = meRes?.data;
      }

      const destinations = [];
      const orgCaps = new Set(meData?.capabilities || []);
      const canOrgPost =
        Boolean(meData?.is_owner) || orgCaps.has("org.post");
      setCanOrgPostCritical(canOrgPost);
      setCanManageOrgSettings(
        Boolean(meData?.is_owner) ||
          meData?.role === "admin" ||
          orgCaps.has("org.manage_settings")
      );
      if (canOrgPost) {
        destinations.push({
          type: "org",
          id: orgId,
          label: "Organization",
        });
      }

      const memberTeams = Array.isArray(membershipsData)
        ? membershipsData
        : [];
      const nextAdminIds = new Set();

      memberTeams.forEach((team) => {
        const id = team.team_id || team.teamId;
        if (!id) return;
        if (team.role === "admin") nextAdminIds.add(Number(id));
        destinations.push({
          type: "team",
          id,
          label: team.name || `Team ${id}`,
        });
      });
      setAdminTeamIds(nextAdminIds);

      // Prefer teams first in the select (org stays available)
      const orgDest = destinations.filter((d) => d.type === "org");
      const teamDest = destinations.filter((d) => d.type === "team");
      setComposerDestinations([...teamDest, ...orgDest]);
    } catch (err) {
      console.error("Failed to load composer destinations:", err);
      setComposerDestinations([]);
      setCanOrgPostCritical(false);
      setCanManageOrgSettings(false);
      setAdminTeamIds(new Set());
    }
  };

  const handleComposerSubmit = async (payload) => {
    if (!accessToken || isCreatingPost) return;

    // Social home: personal public post
    if (selectedOrgId === "social" || selectedOrgId === null) {
      setIsCreatingPost(true);
      try {
        const { data: created } = await axios.post(
          `${API_BASE_URL}/social/posts`,
          {
            title: payload.title,
            content: payload.content,
            visibility: "public",
          },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const createdPostId =
          created?.social_post_id ?? created?.post_id ?? created?.postId;
        let withMedia = created;
        if (createdPostId != null && payload.files?.length) {
          try {
            let result;
            for (const file of payload.files) {
              result = await uploadSocialPostMedia(accessToken, createdPostId, file);
            }
            withMedia = { ...created, media: result?.media ?? withMedia.media };
          } catch (mediaErr) {
            console.error("Failed to upload post media:", mediaErr);
            toastError(mediaErr, "Posted, but media upload failed");
          }
        }
        if (createdPostId != null && payload.embedUrl) {
          try {
            const embedResult = await setSocialEmbed(accessToken, createdPostId, payload.embedUrl);
            withMedia = { ...withMedia, media: embedResult?.media ?? withMedia.media };
          } catch (embedErr) {
            console.error("Failed to set video embed:", embedErr);
            toastError(embedErr, "Posted, but video link failed");
          }
        }
        const normalized = normalizeSocialHomePost(withMedia);
        setPosts((prev) => [normalized, ...prev]);
        setSocialSuggestions(null);
        setComposerFormKey((k) => k + 1);
        toastSuccess("Posted");
      } catch (err) {
        toastError(
          err.response?.data?.message || err.message || "Could not post"
        );
        throw err;
      } finally {
        setIsCreatingPost(false);
      }
      return;
    }

    setIsCreatingPost(true);
    try {
      const body = {
        title: payload.title,
        content: payload.content,
        tags: payload.tags,
        critical: Boolean(payload.critical),
        references: payload.references,
      };
      if (payload.team_id != null) {
        body.team_id = payload.team_id;
        body.org_id = selectedOrgId;
      } else {
        body.org_id = payload.org_id || selectedOrgId;
      }

      const { data: created } = await axios.post(`${API_BASE_URL}/posts`, body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const createdPostId = created?.post_id ?? created?.postId;
      let withAttachments = created;
      if (createdPostId != null && payload.files?.length) {
        try {
          const attachments = await uploadPostAttachments({
            postId: createdPostId,
            orgId: selectedOrgId,
            files: payload.files,
            accessToken,
          });
          withAttachments = { ...created, attachments };
        } catch (attachErr) {
          console.error("Failed to upload post attachments:", attachErr);
          toastEntitlementOrError(
            attachErr,
            "Post created, but file upload failed",
            {
              canManageSettings: canManageOrgSettings,
              orgId: selectedOrgId,
            }
          );
        }
      }

      // Attach author if API hasn't hydrated yet
      const withAuthor =
        withAttachments && !withAttachments.user && user
          ? { ...withAttachments, user }
          : withAttachments;

      const feedData = await makeAuthenticatedRequest((token) =>
        fetchFeed(token, selectedOrgId, 0, {
          catchupWindow: catchupBehindRef.current,
        })
      );
      const { pinned, posts: regular } = normalizeFeedResponse(feedData);
      setPosts(
        prependCreatedPost(mergePinnedFeed(pinned, regular), withAuthor)
      );
      feedOffsetRef.current = regular.length;
      setHasMore(regular.length >= FEED_PAGE_SIZE);
      setComposerFormKey((k) => k + 1);
      const newId = withAuthor?.post_id ?? withAuthor?.postId;
      if (newId != null) {
        setHighlightedPostId(newId);
        window.setTimeout(() => {
          setHighlightedPostId((cur) =>
            String(cur) === String(newId) ? null : cur
          );
        }, 1600);
        requestAnimationFrame(() => {
          const el = document.getElementById(feedPostAnchorId(newId));
          el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      }
    } catch (err) {
      console.error("Failed to create post:", err);
      toastError(err, "Failed to create post");
      throw err;
    } finally {
      setIsCreatingPost(false);
    }
  };

  const handleComposerSaveDraft = async (payload) => {
    if (!accessToken) return;

    if (selectedOrgId === "social" || selectedOrgId === null) {
      setIsCreatingPost(true);
      try {
        const { data: created } = await axios.post(
          `${API_BASE_URL}/social/posts`,
          {
            title: payload.title,
            content: payload.content,
            visibility: "public",
            is_draft: true,
          },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const draftPostId =
          created?.social_post_id ?? created?.post_id ?? created?.postId;
        if (draftPostId != null && payload.files?.length) {
          try {
            for (const file of payload.files) {
              await uploadSocialPostMedia(accessToken, draftPostId, file);
            }
          } catch (mediaErr) {
            console.error("Failed to upload draft media:", mediaErr);
            toastError(mediaErr, "Draft saved, but media upload failed");
          }
        }
        if (draftPostId != null && payload.embedUrl) {
          try {
            await setSocialEmbed(accessToken, draftPostId, payload.embedUrl);
          } catch (embedErr) {
            console.error("Failed to set draft video embed:", embedErr);
            toastError(embedErr, "Draft saved, but video link failed");
          }
        }
        setComposerFormKey((k) => k + 1);
        toastSuccess("Draft saved");
      } catch (err) {
        console.error("Failed to save social draft:", err);
        toastError(err, "Failed to save draft");
        throw err;
      } finally {
        setIsCreatingPost(false);
      }
      return;
    }

    if (!selectedOrgId || selectedOrgId === "social") return;

    setIsCreatingPost(true);
    try {
      const body = {
        title: payload.title,
        content: payload.content,
        tags: payload.tags,
        critical: Boolean(payload.critical),
        references: payload.references,
        is_draft: true,
      };
      if (payload.team_id != null) {
        body.team_id = payload.team_id;
        body.org_id = selectedOrgId;
      } else {
        body.org_id = payload.org_id || selectedOrgId;
      }

      const { data: created } = await axios.post(`${API_BASE_URL}/posts`, body, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const draftId = created?.post_id ?? created?.postId;
      if (draftId != null && payload.files?.length) {
        try {
          await uploadPostAttachments({
            postId: draftId,
            orgId: selectedOrgId,
            files: payload.files,
            accessToken,
          });
        } catch (attachErr) {
          console.error("Failed to upload draft attachments:", attachErr);
          toastEntitlementOrError(
            attachErr,
            "Draft saved, but file upload failed",
            {
              canManageSettings: canManageOrgSettings,
              orgId: selectedOrgId,
            }
          );
        }
      }
      setComposerFormKey((k) => k + 1);
      toastSuccess("Draft saved");
    } catch (err) {
      console.error("Failed to save draft:", err);
      toastError(err, "Failed to save draft");
      throw err;
    } finally {
      setIsCreatingPost(false);
    }
  };

  // API helper function to fetch post interactions
  const fetchPostInteractions = async (postId, token) => {
    const response = await axios.get(
      `${postsApiBase}/${postId}/interactions`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params:
          selectedOrgId && selectedOrgId !== "social"
            ? { org_id: selectedOrgId }
            : {},
      }
    );
    return response.data;
  };

  // API helper function to create interaction
  const createInteraction = async (postId, interactionData, token) => {
    const response = await axios.post(
      `${postsApiBase}/${postId}/interactions`,
      interactionData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params:
          selectedOrgId && selectedOrgId !== "social"
            ? { org_id: selectedOrgId }
            : {},
      }
    );
    return response.data;
  };

  // Calculate interaction counts for a post
  // Pass forceRefresh=true after mutations so we never return a stale cache entry
  const getInteractionCounts = async (postId, { forceRefresh = false } = {}) => {
    const id = String(postId);
    if (!forceRefresh && interactionCounts[id]) {
      return {
        ...interactionCounts[id],
        isLiked: likedPosts.has(id),
      };
    }

    try {
      const interactions = await makeAuthenticatedRequest(
        (token) => fetchPostInteractions(id, token)
      );

      const counts = {
        likeCount: interactions.filter(
          (i) =>
            (i.interactionType === "like" || i.interaction_type === "like") &&
            !(i.parentId ?? i.parent_id)
        ).length,
        commentCount: interactions.filter(
          (i) => i.interactionType === "comment" || i.interaction_type === "comment"
        ).length,
      };

      let isLiked = false;
      if (user) {
        isLiked = interactions.some(
          (i) =>
            (i.interactionType === "like" || i.interaction_type === "like") &&
            !(i.parentId ?? i.parent_id) &&
            (i.userId === user.user_id || i.user_id === user.user_id)
        );
      }

      setInteractionCounts((prev) => ({
        ...prev,
        [id]: counts,
      }));

      setLikedPosts((prev) => {
        const next = new Set(prev);
        if (isLiked) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });

      return { ...counts, isLiked };
    } catch (err) {
      console.error("Failed to fetch interaction counts:", err);
      return { likeCount: 0, commentCount: 0, isLiked: false };
    }
  };

  // Handle like action with optimistic updates
  const handleLike = async (postId) => {
    if (!accessToken || !user) return;
    const id = String(postId);

    const currentCounts = interactionCounts[id] || {
      likeCount: 0,
      commentCount: 0,
    };
    const isCurrentlyLiked = likedPosts.has(id);

    // Optimistic: toggle heart + count immediately
    if (isCurrentlyLiked) {
      setInteractionCounts((prev) => ({
        ...prev,
        [id]: {
          ...currentCounts,
          likeCount: Math.max(0, currentCounts.likeCount - 1),
        },
      }));
      setLikedPosts((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } else {
      setInteractionCounts((prev) => ({
        ...prev,
        [id]: {
          ...currentCounts,
          likeCount: currentCounts.likeCount + 1,
        },
      }));
      setLikedPosts((prev) => new Set([...prev, id]));
    }

    try {
      if (isCurrentlyLiked) {
        const interactions = await makeAuthenticatedRequest((token) =>
          fetchPostInteractions(id, token)
        );

        const userLike = interactions.find(
          (i) =>
            (i.interactionType === "like" || i.interaction_type === "like") &&
            !(i.parentId ?? i.parent_id) &&
            (i.userId === user.user_id || i.user_id === user.user_id)
        );

        if (userLike) {
          await makeAuthenticatedRequest(async (token) => {
            await axios.delete(
              `${interactionsDeleteBase}/${
                userLike.interactionId || userLike.interaction_id
              }`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                params:
                  selectedOrgId && selectedOrgId !== "social"
                    ? { org_id: selectedOrgId }
                    : {},
              }
            );
          });
        }
      } else {
        await makeAuthenticatedRequest(async (token) => {
          await createInteraction(id, { interaction_type: "like" }, token);
        });
      }

      // Confirm with a forced network refresh
      await getInteractionCounts(id, { forceRefresh: true });
    } catch (err) {
      console.error("Failed to toggle like:", err);
      // Revert optimistic update
      setInteractionCounts((prev) => ({
        ...prev,
        [id]: currentCounts,
      }));
      setLikedPosts((prev) => {
        const next = new Set(prev);
        if (isCurrentlyLiked) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });

      if (
        err.response?.status === 400 &&
        err.response?.data?.message?.includes("already liked")
      ) {
        await getInteractionCounts(id, { forceRefresh: true });
      } else {
        toastError("Failed to update like. Please try again.");
      }
    }
  };

  // Load all data
  useEffect(() => {
    if (!accessToken || !workspaceReady) return;
    if (workspaceUpdating) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user data
        const userData = await makeAuthenticatedRequest(fetchUserData);
        setUser(userData);

        // Check if viewing org feed (not personal feed)
        const isSocialFeed = selectedOrgId === "social" || selectedOrgId === null;
        
        // If viewing org feed, fetch org members immediately (in parallel with posts)
        if (!isSocialFeed) {
          // Fetch org members for selectedOrgId immediately
          makeAuthenticatedRequest((token) => fetchOrgMembers(selectedOrgId, token))
            .then(members => {
              const maps = {};
              maps[selectedOrgId] = {};
              if (members && Array.isArray(members)) {
                members.forEach(member => {
                  if (member.user && member.user.user_id) {
                    maps[selectedOrgId][member.user.user_id] = member.title || null;
                  }
                });
              }
              setOrgMembersMaps(maps);
            })
            .catch(err => {
              console.error(`Failed to fetch members for org ${selectedOrgId}:`, err);
              // Don't break - just log error, orgMembersMap will be empty
            });

          makeAuthenticatedRequest((token) =>
            loadComposerDestinations(selectedOrgId, token, userData.user_id)
          ).catch((err) => {
            console.error("Failed to load composer destinations:", err);
          });
        } else {
          // Clear org members map for personal feed
          setOrgMembersMaps({});
          setComposerDestinations([]);
          setCanOrgPostCritical(false);
          setCanManageOrgSettings(false);
          setAdminTeamIds(new Set());
        }

        // Fetch feed posts (in parallel with org members)
        const feedData = await makeAuthenticatedRequest((token) =>
          fetchFeed(token, selectedOrgId, 0, {
            catchupWindow: catchupBehindRef.current,
          })
        );
        if (isSocialFeed) {
          const rawPosts = Array.isArray(feedData?.posts)
            ? feedData.posts
            : Array.isArray(feedData)
              ? feedData
              : [];
          const normalized = rawPosts.map((p) => normalizeSocialHomePost(p));
          setPosts(normalized);
          setLikedPosts(seedLikedFromPosts(normalized));
          setSocialSuggestions(feedData?.suggestions || null);
          feedOffsetRef.current = normalized.length;
          setHasMore(false);
        } else {
          setSocialSuggestions(null);
          const { pinned, posts: regular } = normalizeFeedResponse(feedData);
          setPosts(mergePinnedFeed(pinned, regular));
          feedOffsetRef.current = regular.length;
          setHasMore(regular.length >= FEED_PAGE_SIZE);
        }

        // Pre-fetch interaction counts for all posts (optional, can be lazy loaded)
        // For now, we'll lazy load when PostCard needs them
        // This could be optimized to fetch in parallel

        setLoading(false);
      } catch (err) {
        console.error("Failed to load data:", err);
        
        // If error is due to invalid orgId (401 Unauthorized or 404 Not Found), fall back to personal feed
        if (selectedOrgId && selectedOrgId !== "social" && selectedOrgId !== null) {
          if (err.response?.status === 401 || err.response?.status === 404) {
            console.log("Invalid orgId loaded from localStorage, falling back to personal feed");
            setSelectedOrgId("social");
            // Don't set error, just switch to personal feed
            return;
          }
        }
        
        setError(err.message || "Failed to load feed data");
        setLoading(false);
      }
    };

    loadData();
    // Intentionally omit request helpers — reload when workspace/auth identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    selectedOrgId,
    workspaceReady,
    workspaceUpdating,
    workspaceRefreshKey,
  ]);

  // Keep the id set in sync so load-more can dedupe without depending on posts.
  useEffect(() => {
    seenIdsRef.current = new Set(
      posts.map((p) => String(p.post_id ?? p.postId))
    );
  }, [posts]);

  // Reset catch-up bias when switching orgs / social.
  useEffect(() => {
    catchupBehindRef.current = false;
  }, [selectedOrgId]);

  const handleCatchupAwarenessChange = useCallback(
    async ({ behind }) => {
      const next = Boolean(behind);
      const prev = catchupBehindRef.current;
      if (next === prev) return;
      catchupBehindRef.current = next;
      if (
        !accessToken ||
        !selectedOrgId ||
        selectedOrgId === "social" ||
        !workspaceReady ||
        workspaceUpdating
      ) {
        return;
      }
      // Quiet page-0 refetch when bias toggles. Do not setLoading — that
      // unmounts CatchUpCard and snaps Show summary back shut.
      try {
        const feedData = await makeAuthenticatedRequest((token) =>
          fetchFeed(token, selectedOrgId, 0, { catchupWindow: next })
        );
        const { pinned, posts: regular } = normalizeFeedResponse(feedData);
        setPosts(mergePinnedFeed(pinned, regular));
        feedOffsetRef.current = regular.length;
        setHasMore(regular.length >= FEED_PAGE_SIZE);
      } catch (err) {
        console.error("Failed to refetch feed after catch-up awareness:", err);
      }
    },
    // Intentionally omit request helpers — refetch when workspace/auth identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, selectedOrgId, workspaceReady, workspaceUpdating]
  );

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || loading) return;
    const offset = feedOffsetRef.current;
    setLoadingMore(true);
    try {
      const data = await makeAuthenticatedRequest((token) =>
        fetchFeed(token, selectedOrgId, offset, {
          catchupWindow: catchupBehindRef.current,
        })
      );
      const { posts: regular } = normalizeFeedResponse(data);
      const fresh = regular.filter(
        (p) => !seenIdsRef.current.has(String(p.post_id ?? p.postId))
      );
      if (fresh.length === 0) {
        setHasMore(false);
        return;
      }
      feedOffsetRef.current = offset + regular.length;
      if (regular.length < FEED_PAGE_SIZE) {
        setHasMore(false);
      }
      setPosts((prev) => [...prev, ...fresh]);
    } catch (err) {
      console.error("Failed to load more feed:", err);
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMore, loading, selectedOrgId, accessToken]);

  // Trigger load-more when the sentinel scrolls into view.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { root: scrollContainerRef.current || null, rootMargin: "400px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  // Legacy ?post= → canonical permalink (workplace org only)
  useEffect(() => {
    const postIdFromUrl = searchParams.get("post");
    if (
      !postIdFromUrl ||
      !selectedOrgId ||
      selectedOrgId === "social"
    ) {
      return;
    }
    const params = new URLSearchParams();
    if (searchParams.get("from") === "catchup") {
      params.set("from", "catchup");
    }
    const returnTo = searchParams.get("return");
    if (returnTo) params.set("return", returnTo);
    const qs = params.toString();
    navigate(
      `${permalinkForPost({ orgId: selectedOrgId, postId: postIdFromUrl })}${
        qs ? `?${qs}` : ""
      }`,
      { replace: true }
    );
  }, [searchParams, selectedOrgId, navigate]);

  const openPostThread = (post) => {
    const postId = post?.post_id || post?.postId;
    if (postId == null) return;
    const returnTo = pathWithPostAnchor(
      `${location.pathname}${location.search || ""}` || "/dashboard",
      postId
    );
    if (selectedOrgId && selectedOrgId !== "social") {
      const href = permalinkForPost({ orgId: selectedOrgId, postId });
      navigate(withReturnQuery(href, returnTo));
      return;
    }
    navigate(socialThreadHref({ postId, returnPath: returnTo }));
  };

  useScrollToFeedPostAnchor(!loading);

  // Backend now handles filtering, so we can use posts directly
  // Normalize posts to handle both personal and org post structures
  const filteredPosts = useMemo(() => {
    const isSocialFeed =
      selectedOrgId === "social" || selectedOrgId === null;
    return posts.map((post) => {
      // Social feed: keep API/normalizeSocialHomePost shape intact (esp. quote
      // embeds). Do not invent titles from content — that broke quote cards.
      if (isSocialFeed) {
        const id =
          post.post_id ||
          post.postId ||
          post.socialPostId ||
          post.social_post_id;
        return {
          ...post,
          postId: id,
          post_id: id,
          createdAt: post.createdAt || post.created_at,
          created_at: post.created_at || post.createdAt,
        };
      }

      const isSocialRepost =
        post.repost_kind === "bare" || post.repost_kind === "quote";
      return {
        ...post,
        postId:
          post.postId ||
          post.post_id ||
          post.socialPostId ||
          post.social_post_id,
        post_id:
          post.post_id ||
          post.postId ||
          post.socialPostId ||
          post.social_post_id,
        createdAt: post.createdAt || post.created_at,
        created_at: post.created_at || post.createdAt,
        user: post.user || post.author,
        content: post.content || post.body || "",
        // Keep empty titles silent (tweet-style); never invent from body.
        title: isSocialRepost
          ? post.repost_kind === "quote"
            ? null
            : post.title ?? null
          : post.title?.trim()
            ? post.title
            : null,
        kanbanStatus: post.kanbanStatus || post.kanban_status,
        kanban_status: post.kanban_status || post.kanbanStatus,
        assignees: post.assignees || [],
        assignee_ids: post.assignee_ids || post.assigneeIds || [],
        assigneeIds: post.assigneeIds || post.assignee_ids || [],
      };
    });
  }, [posts, selectedOrgId]);

  return (
    <div className="flex flex-col h-full bg-background text-foreground">
      {/* Page header — Social title aligns with feed/composer column */}
      <header
        className={
          isSocialWorkspace
            ? "hidden md:block border-b border-border px-4 py-3 md:px-6 md:py-4"
            : "hidden md:flex items-center gap-4 border-b border-border p-4"
        }
      >
        {isSocialWorkspace ? (
          <div className="mx-auto w-full max-w-7xl flex min-w-0 gap-6 xl:gap-8 items-center justify-start">
            <div
              className="hidden xl:block w-[220px] shrink-0"
              aria-hidden
            />
            <div className="w-full max-w-2xl flex-1 min-w-0 text-center">
              <h2 className="text-xl font-bold truncate">
                {socialProfile?.display_name
                  ? `Home · ${socialProfile.display_name}`
                  : "Home"}
              </h2>
            </div>
            <div
              className="hidden 2xl:block w-[280px] shrink-0"
              aria-hidden
            />
          </div>
        ) : (
          <h2 className="text-xl font-bold">
            {`Updates for ${getFullName(user)}`}
          </h2>
        )}
      </header>

      {/* Main Content */}
      <main
        ref={scrollContainerRef}
        className="flex-1 flex flex-col px-4 py-3 md:p-6 overflow-y-auto"
      >

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && (
          <div className="text-center text-muted-foreground py-8">
            Loading feed...
          </div>
        )}

        {!loading && (
          <div
            className={
              isSocialWorkspace
                ? "mx-auto w-full max-w-7xl flex min-w-0 gap-6 xl:gap-8 items-start justify-start"
                : undefined
            }
          >
          {isSocialWorkspace ? (
            <SocialHomeLeftRail accessToken={accessToken} />
          ) : null}
          <section
            className={
              isSocialWorkspace
                ? "w-full max-w-2xl flex-1 min-w-0 space-y-4"
                : "mx-auto w-full max-w-2xl min-w-0 space-y-4"
            }
          >
            {selectedOrgId && selectedOrgId !== "social" && (
              <CatchUpCard
                orgId={selectedOrgId}
                scope="org"
                accessToken={accessToken}
                canManageSettings={canManageOrgSettings}
                onAwarenessChange={handleCatchupAwarenessChange}
              />
            )}
            {selectedOrgId && selectedOrgId !== "social" && (
              composerDestinations.length > 0 ? (
                <PostComposer
                  mode="collapsed"
                  destinationMode="picker"
                  destinations={composerDestinations}
                  orgId={selectedOrgId}
                  isSubmitting={isCreatingPost}
                  onSubmit={handleComposerSubmit}
                  onSaveDraft={handleComposerSaveDraft}
                  formKey={composerFormKey}
                  allowCritical={(dest) => {
                    if (!dest) return false;
                    if (canOrgPostCritical) return true;
                    if (dest.type === "team") {
                      return adminTeamIds.has(Number(dest.id));
                    }
                    return false;
                  }}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  Join a team or become an org member to share an update here.
                </p>
              )
            )}
            {(selectedOrgId === "social" || selectedOrgId === null) && (
              <>
                <SocialHomeMobileMore accessToken={accessToken} />
                <PostComposer
                  mode="collapsed"
                  destinationMode="fixed"
                  hideDestination
                  hideTags
                  inferTitle={false}
                  fixedTeamId="personal"
                  fixedTeamLabel="Your followers"
                  contentPlaceholder="Share with your followers…"
                  isSubmitting={isCreatingPost}
                  onSubmit={handleComposerSubmit}
                  onSaveDraft={handleComposerSaveDraft}
                  formKey={composerFormKey}
                  allowCritical={false}
                  submitLabel="Post"
                />
              </>
            )}
            {filteredPosts.length === 0 ? (
              <div className="space-y-6">
                <p className="text-center text-muted-foreground">
                  {selectedOrgId && selectedOrgId !== "social"
                    ? "No posts available for the selected organization."
                    : "Your feed is empty. Follow people or join communities to see posts here."}
                </p>
                {(selectedOrgId === "social" || selectedOrgId === null) && (
                  <div className="space-y-4 rounded-lg border border-border p-4">
                    <div className="flex flex-wrap gap-2 justify-center">
                      <Button asChild variant="default" size="sm">
                        <Link to="/explore">Explore communities</Link>
                      </Button>
                      <Button asChild variant="outline" size="sm">
                        <Link to="/explore">Find people</Link>
                      </Button>
                    </div>
                    {socialSuggestions?.communities?.length ? (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Suggested communities</p>
                        <ul className="space-y-2">
                          {socialSuggestions.communities.slice(0, 4).map((c) => (
                            <li key={c.community_id}>
                              <Link
                                to={`/c/${c.community_id}`}
                                className="text-sm text-primary hover:underline"
                              >
                                {c.display_name}
                                {c.join_policy === "invite_only"
                                  ? " · Invite only"
                                  : ""}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {socialSuggestions?.users?.length ? (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">People to follow</p>
                        <ul className="space-y-2">
                          {socialSuggestions.users.slice(0, 4).map((u) => (
                            <li key={u.username}>
                              <Link
                                to={`/u/${u.username}`}
                                className="text-sm text-primary hover:underline"
                              >
                                {u.display_name}{" "}
                                <span className="text-muted-foreground">
                                  @{u.username}
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : (
              filteredPosts.flatMap((post, index) => {
                const postId = post.post_id || post.postId;
                const likeTargetId = String(socialLikeTargetId(post));
                const counts = interactionCounts[likeTargetId] || {
                  likeCount: post.like_count ?? post.likeCount ?? 0,
                  commentCount: post.reply_count ?? post.replyCount ?? 0,
                };
                
                // Use selectedOrgId for orgMembersMap (all posts in org feed belong to selectedOrgId)
                // For personal feed, orgMembersMap will be empty {}
                const isSocialFeed = selectedOrgId === "social" || selectedOrgId === null;
                const orgMembersMap = !isSocialFeed && selectedOrgId 
                  ? (orgMembersMaps[selectedOrgId] || {}) 
                  : {};
                
                // Lazy load interaction counts when PostCard is rendered
                if (!interactionCounts[likeTargetId] && accessToken) {
                  getInteractionCounts(likeTargetId).catch(console.error);
                }
                
                const card = (
                  <PostCard
                    key={postId}
                    post={post}
                    onExpand={() =>
                      openPostThread(
                        post.repost_kind === "bare" && post.repost_of
                          ? post.repost_of
                          : post
                      )
                    }
                    likeCount={counts.likeCount}
                    commentCount={counts.commentCount}
                    accessToken={accessToken}
                    onLike={() => handleLike(likeTargetId)}
                    onComment={() =>
                      openPostThread(
                        post.repost_kind === "bare" && post.repost_of
                          ? post.repost_of
                          : post
                      )
                    }
                    isLiked={likedPosts.has(likeTargetId)}
                    orgMembersMap={orgMembersMap}
                    authorAboveTitle={isSocialFeed}
                    socialHome={isSocialFeed}
                    relativeTimestamps
                    socialTombstone={isSocialFeed}
                    socialRepost={isSocialFeed}
                    onQuoteCreated={(created) => {
                      if (!isSocialFeed || !created) return;
                      const normalized = normalizeSocialHomePost(created);
                      setPosts((prev) => [normalized, ...prev]);
                    }}
                    onRepostChange={(info) => {
                      if (!isSocialFeed) return;
                      if (info?.unreposted && info.root_id != null) {
                        setPosts((prev) =>
                          prev.filter(
                            (p) =>
                              !(
                                p.repost_kind === "bare" &&
                                String(
                                  p.repost_of_social_post_id ??
                                    p.repost_of?.post_id
                                ) === String(info.root_id)
                              )
                          )
                        );
                      }
                      if (info?.post && !info.unreposted) {
                        const normalized = normalizeSocialHomePost(info.post);
                        setPosts((prev) => [normalized, ...prev]);
                      }
                      const rootId = info?.root_id;
                      if (rootId != null && info.repost_count != null) {
                        setPosts((prev) =>
                          prev.map((p) => {
                            const pid = p.post_id;
                            const rid =
                              p.repost_of?.post_id ?? p.repost_of_social_post_id;
                            if (String(pid) === String(rootId)) {
                              return {
                                ...p,
                                repost_count: info.repost_count,
                                viewer_has_reposted: info.viewer_has_reposted,
                              };
                            }
                            // Bare wrapper pointing at the amplify target
                            if (
                              p.repost_kind === "bare" &&
                              String(rid) === String(rootId) &&
                              p.repost_of
                            ) {
                              return {
                                ...p,
                                repost_of: {
                                  ...p.repost_of,
                                  repost_count: info.repost_count,
                                  viewer_has_reposted: info.viewer_has_reposted,
                                },
                              };
                            }
                            return p;
                          })
                        );
                      }
                    }}
                    onEmbedLike={() => {
                      const root = socialQuoteEmbedRoot(post);
                      if (!root) return;
                      return handleLike(root.social_post_id ?? root.post_id);
                    }}
                    onEmbedExpand={() => {
                      const root = socialQuoteEmbedRoot(post);
                      if (root) openPostThread(root);
                    }}
                    embedIsLiked={(() => {
                      const root = socialQuoteEmbedRoot(post);
                      return root
                        ? likedPosts.has(
                            String(root.social_post_id ?? root.post_id)
                          )
                        : false;
                    })()}
                    highlighted={
                      highlightedPostId != null &&
                      String(highlightedPostId) === String(postId)
                    }
                  />
                );

                const nodes = [card];
                if (
                  isSocialFeed &&
                  (index + 1) % 8 === 0 &&
                  socialSuggestions?.communities?.length
                ) {
                  nodes.push(
                    <SocialHomeDiscoveryStrip
                      key={`discover-after-${postId}`}
                      communities={socialSuggestions.communities}
                      paths={socialPaths}
                    />
                  );
                }
                return nodes;
              })
            )}

            {filteredPosts.length > 0 && (
              <div
                ref={loadMoreRef}
                className="py-6 text-center text-sm text-muted-foreground"
              >
                {loadingMore
                  ? "Loading more…"
                  : hasMore
                  ? ""
                  : "You're all caught up."}
              </div>
            )}
          </section>
          {isSocialWorkspace ? (
            <SocialHomeRail accessToken={accessToken} />
          ) : null}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
